import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getGoogleAdsConnectService,
  type GoogleAdsConnectApi,
} from '../services/GoogleAdsConnectService';
import type {
  GoogleAdsConnectionStatus,
  GoogleAdsConnectorRuntimeStatus,
  GoogleAdsReportingAccount,
} from '../types';

export type GoogleAdsRowSelection = Record<string, boolean>;
export type GoogleAdsAction = 'connect' | 'disconnect' | 'save' | null;

function selectionFromAccounts(accounts: GoogleAdsReportingAccount[]): GoogleAdsRowSelection {
  return Object.fromEntries(
    accounts
      .filter((account) => account.selected)
      .map((account) => [account.identity.providerAccountId, true]),
  );
}

function selectedIds(selection: GoogleAdsRowSelection): string[] {
  return Object.entries(selection)
    .filter(([, selected]) => selected)
    .map(([customerId]) => customerId)
    .sort();
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const normalizedLeft = [...left].sort();
  const normalizedRight = [...right].sort();
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
}

export function createGoogleAdsConnectionId(): string {
  const randomId = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `google-ads-${randomId}`;
}

export function useGoogleAdsConnect(
  projectId: string,
  service: GoogleAdsConnectApi = getGoogleAdsConnectService(),
) {
  const [connections, setConnections] = useState<GoogleAdsConnectionStatus[]>([]);
  const [runtime, setRuntime] = useState<GoogleAdsConnectorRuntimeStatus | null>(null);
  const [activeConnectionId, setActiveConnectionId] = useState('');
  const [accounts, setAccounts] = useState<GoogleAdsReportingAccount[]>([]);
  const [rowSelection, setRowSelection] = useState<GoogleAdsRowSelection>({});
  const [persistedCustomerIds, setPersistedCustomerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [action, setAction] = useState<GoogleAdsAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const connectionSequence = useRef(0);
  const accountSequence = useRef(0);
  const connectionAbort = useRef<AbortController | null>(null);
  const accountAbort = useRef<AbortController | null>(null);

  const activeConnection = useMemo(
    () => connections.find((connection) => connection.connectionId === activeConnectionId) || null,
    [activeConnectionId, connections],
  );
  const selectedCustomerIds = useMemo(() => selectedIds(rowSelection), [rowSelection]);
  const isDirty = useMemo(
    () => !sameIds(selectedCustomerIds, persistedCustomerIds),
    [persistedCustomerIds, selectedCustomerIds],
  );

  const loadConnections = useCallback(async () => {
    connectionAbort.current?.abort();
    const controller = new AbortController();
    connectionAbort.current = controller;
    const sequence = ++connectionSequence.current;
    setLoading(true);
    setError(null);
    try {
      const response = await service.listConnections(projectId, controller.signal);
      if (sequence !== connectionSequence.current) return;
      setConnections(response.connections);
      setRuntime(response.runtime);
      setActiveConnectionId((current) => {
        if (response.connections.some((connection) => connection.connectionId === current)) {
          return current;
        }
        const callbackConnectionId = typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('googleAdsConnectionId')
          : null;
        return response.connections.find((connection) => connection.connectionId === callbackConnectionId)?.connectionId
          || response.connections.find((connection) => connection.connected && !connection.needsReconnect)?.connectionId
          || response.connections[0]?.connectionId
          || '';
      });
    } catch (loadError) {
      if (controller.signal.aborted || sequence !== connectionSequence.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Failed to load Google Ads connections');
    } finally {
      if (sequence === connectionSequence.current) setLoading(false);
    }
  }, [projectId, service]);

  const loadAccounts = useCallback(async (connectionId: string) => {
    accountAbort.current?.abort();
    const controller = new AbortController();
    accountAbort.current = controller;
    const sequence = ++accountSequence.current;
    setAccountsLoading(true);
    setError(null);
    try {
      const discovered = await service.listAccounts(projectId, connectionId, controller.signal);
      if (sequence !== accountSequence.current) return;
      const persisted = selectedIds(selectionFromAccounts(discovered));
      setAccounts(discovered);
      setRowSelection(selectionFromAccounts(discovered));
      setPersistedCustomerIds(persisted);
    } catch (loadError) {
      if (controller.signal.aborted || sequence !== accountSequence.current) return;
      setAccounts([]);
      setRowSelection({});
      setPersistedCustomerIds([]);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load Google Ads accounts');
    } finally {
      if (sequence === accountSequence.current) setAccountsLoading(false);
    }
  }, [projectId, service]);

  useEffect(() => {
    void loadConnections();
    return () => {
      connectionAbort.current?.abort();
      accountAbort.current?.abort();
    };
  }, [loadConnections]);

  useEffect(() => {
    if (!activeConnection) {
      accountAbort.current?.abort();
      setAccounts([]);
      setRowSelection({});
      setPersistedCustomerIds([]);
      return;
    }
    if (!activeConnection.connected || activeConnection.needsReconnect || !activeConnection.connectorEnabled) {
      accountAbort.current?.abort();
      setAccounts([]);
      setRowSelection({});
      setPersistedCustomerIds(activeConnection.selectedCustomerIds);
      return;
    }
    void loadAccounts(activeConnection.connectionId);
  }, [activeConnection, loadAccounts]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('googleAds') === 'connected') {
      setNotice('Google Ads connected. Choose the advertiser accounts Segmently may read.');
    } else if (params.get('googleAds') === 'error') {
      setError('Google Ads authorization was not completed.');
    }
  }, []);

  const chooseConnection = useCallback((connectionId: string) => {
    accountAbort.current?.abort();
    setAccounts([]);
    setRowSelection({});
    setPersistedCustomerIds([]);
    setNotice(null);
    setError(null);
    setActiveConnectionId(connectionId);
  }, []);

  const connect = useCallback(async (connectionId?: string) => {
    setAction('connect');
    setError(null);
    setNotice(null);
    try {
      const id = connectionId || createGoogleAdsConnectionId();
      const returnTo = `/project/${encodeURIComponent(projectId)}/integrations`;
      const url = await service.getOAuthUrl(projectId, id, returnTo);
      window.location.assign(url);
      setAction(null);
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : 'Failed to start Google Ads authorization');
      setAction(null);
    }
  }, [projectId, service]);

  const disconnect = useCallback(async () => {
    if (!activeConnection) return;
    setAction('disconnect');
    setError(null);
    setNotice(null);
    try {
      await service.disconnect(projectId, activeConnection.connectionId);
      setNotice('Google Ads connection disconnected from this project.');
      await loadConnections();
    } catch (disconnectError) {
      setError(disconnectError instanceof Error ? disconnectError.message : 'Failed to disconnect Google Ads');
    } finally {
      setAction(null);
    }
  }, [activeConnection, loadConnections, projectId, service]);

  const refreshAccounts = useCallback(async () => {
    if (!activeConnection || isDirty) return;
    setNotice(null);
    await loadAccounts(activeConnection.connectionId);
  }, [activeConnection, isDirty, loadAccounts]);

  const discardChanges = useCallback(() => {
    setRowSelection(Object.fromEntries(persistedCustomerIds.map((customerId) => [customerId, true])));
    setNotice(null);
    setError(null);
  }, [persistedCustomerIds]);

  const saveSelection = useCallback(async () => {
    if (!activeConnection || selectedCustomerIds.length === 0 || selectedCustomerIds.length > 100) return;
    setAction('save');
    setError(null);
    setNotice(null);
    try {
      const managerByCustomerId = Object.fromEntries(
        accounts
          .filter((account) => selectedCustomerIds.includes(account.identity.providerAccountId))
          .flatMap((account) => account.accessProvenance.accessContext.kind === 'manager'
            ? [[
                account.identity.providerAccountId,
                account.accessProvenance.accessContext.managerAccountId,
              ]]
            : []),
      );
      const result = await service.selectAccounts({
        projectId,
        connectionId: activeConnection.connectionId,
        customerIds: selectedCustomerIds,
        managerByCustomerId,
      });
      const [connectionReadback, accountReadback] = await Promise.all([
        service.listConnections(projectId),
        service.listAccounts(projectId, activeConnection.connectionId),
      ]);
      const persistedFromAccounts = accountReadback
        .filter((account) => account.selected)
        .map((account) => account.identity.providerAccountId)
        .sort();
      const persistedFromConnection = connectionReadback.connections
        .find((connection) => connection.connectionId === activeConnection.connectionId)
        ?.selectedCustomerIds || [];
      if (
        !sameIds(result.selectedCustomerIds, selectedCustomerIds)
        || !sameIds(persistedFromAccounts, selectedCustomerIds)
        || !sameIds(persistedFromConnection, selectedCustomerIds)
      ) {
        throw new Error('Google Ads selection readback did not match the saved accounts. Please retry.');
      }
      setConnections(connectionReadback.connections);
      setRuntime(connectionReadback.runtime);
      setAccounts(accountReadback);
      setRowSelection(selectionFromAccounts(accountReadback));
      setPersistedCustomerIds(persistedFromAccounts);
      setNotice(`Saved ${selectedCustomerIds.length} Google Ads account${selectedCustomerIds.length === 1 ? '' : 's'}.`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save Google Ads account selection');
    } finally {
      setAction(null);
    }
  }, [accounts, activeConnection, projectId, selectedCustomerIds, service]);

  return {
    connections,
    runtime,
    activeConnection,
    activeConnectionId,
    accounts,
    rowSelection,
    selectedCustomerIds,
    persistedCustomerIds,
    loading,
    accountsLoading,
    action,
    error,
    notice,
    isDirty,
    setRowSelection,
    clearError: () => setError(null),
    clearNotice: () => setNotice(null),
    chooseConnection,
    connect,
    disconnect,
    refreshConnections: loadConnections,
    refreshAccounts,
    discardChanges,
    saveSelection,
  };
}
